import { useEffect, useState } from "react";
import { ReactComponent as FilterBy } from "assets/images/svgs/filterBy.svg";
import { FiSearch } from "react-icons/fi";
import FilterDrawer from "components/drawers/host-side/FilterDrawer";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes";
import {
  EVENT_STATUS,
  EVENT_TIMEFRAMES,
} from "utils/helpers/enums/shared.enum";
import { AttendeeEventsFilterDTO } from "utils/helpers/models/host-dtos/events-filter.dto";
import { useDebounce } from "hooks/useDebounce";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import EventCard from "components/cards/event-card";
import { useSelector } from "react-redux";
import Pagination from "components/pagination";
import noFoundGif from "assets/images/gifs/noFoundGif.gif";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import useHost from "containers/host/useHooks";
import AttendeeBreadcrumb, { AttendeBreadcrumbLink } from "components/breadcrumbs/AttendeeBreadcrumb";
import useStore from "hooks/useStore";
import eventNotFoundGif from "assets/images/gifs/event-not-found.gif";

const AttendeeExperiences = () => {
  const navigate = useNavigate();

  const breadcrumbLinks: AttendeBreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.attendeeHome },
    {
      title: "Experiences",
      path: siteRoutes.attendeeExperiences,
    },
  ];
  
  const [filters, setFilters] = useState<AttendeeEventsFilterDTO>(() => {
    const defaultFilters = new AttendeeEventsFilterDTO();
    // Set default values for proper tab behavior (All tab - only timeframe)
    defaultFilters.timeFrame = EVENT_TIMEFRAMES.ALL;
    // Don't set status for all tab
    return defaultFilters;
  });

  const { isProfileCompleted } = useStore();

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.attendeeHome, { replace: true });
    }
  }, [isProfileCompleted, navigate]);

  const { getEvents } = useHost();
  const debounceSearch = useDebounce(filters.searchTitle, 1000);
  const [data, setData] = useState<EventDTO[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState<string>("all");
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const allFilterTabs = [
    {
      title: "Recomended",
      value: EVENT_TIMEFRAMES.ALL,
      type: "timeFrame",
      id: "all",
    },
    {
      title: "Upcoming",
      value: EVENT_STATUS.UPCOMING,
      type: "timeFrame",
      id: "upcoming",
    },
    {
      title: "Past",
      value: EVENT_TIMEFRAMES.PAST,
      type: "timeFrame",
      id: "past",
    },
    {
      title: "Wishlist",
      value: EVENT_STATUS.WISHLIST,
      type: "status",
      id: "wishlist",
    },
    {
      title: "Waitlisted",
      value: EVENT_STATUS.WAITLISTED,
      type: "status",
      id: "waitlisted",
    },
    {
      title: "Completed",
      value: EVENT_STATUS.COMPLETED,
      type: "status",
      id: "completed",
    },
  ];

  const toggleDrawer = () => {
    setDrawerOpen(prev => !prev);
  };

  const getAllEvents = (filters: AttendeeEventsFilterDTO) => {
    const queryParams: any = {};
    for (let key in filters) {
      if (
        filters[key]?.toString() &&
        filters[key] !== EVENT_TIMEFRAMES.ALL &&
        filters[key] !== EVENT_STATUS.ALL &&
        filters[key] !== ""
      ) {
        queryParams[key] = filters[key];
      }
    }

    getEvents(setData, queryParams, [], setFilters);

    // Mark initial load as complete after first API call
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    getAllEvents(filters);
  }, [debounceSearch]);

  const handleFilterChange = (event: any) => {
    const { name, value } = event.target;
    filters[name] = value;
    setFilters({ ...filters });
    if (name !== "searchTitle") {
      getAllEvents(filters);
    }
  };

  const handleTabChange = (
    filterName: string,
    value: string,
    tabId: string
  ) => {
    // Reset data immediately to show loading state
    setData([]);

    const newFilters = { ...filters };

    // Clear all drawer-related filters when tab changes
    newFilters.isSession = "";
    // newFilters.ageGroups = "";

    // Set only the selected filter
    newFilters[filterName] = value;

    // Only send timeframe if filterName is "timeFrame", otherwise delete it
    if (filterName === "timeFrame") {
      delete newFilters.status; // Remove status so only timeframe is sent
    } else if (filterName === "status") {
      delete newFilters.timeFrame; // Remove timeframe so only status is sent
    }

    // Reset pagination when changing tabs
    newFilters.page = 0;
    newFilters.size = 12;

    // Clear all applied filters when tab changes
    setAppliedFilters({});

    // Update selected tab
    setSelectedTab(tabId);

    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  const onApplyDrawerFilters = (drawerFilters: any) => {
    const newFilters = { ...filters };
    const newAppliedFilters: any = {};

    for (let key in drawerFilters) {
      if (drawerFilters[key] && drawerFilters[key] !== "") {
        newFilters[key] = drawerFilters[key];
        newAppliedFilters[key] = drawerFilters[key];
      } else {
        delete newFilters[key];
        delete newAppliedFilters[key];
      }
    }

    setAppliedFilters(newAppliedFilters);
    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  const removeAppliedFilter = (filterKey: string) => {
    const newFilters = { ...filters };
    const newAppliedFilters = { ...appliedFilters };

    delete newFilters[filterKey];
    delete newAppliedFilters[filterKey];

    setAppliedFilters(newAppliedFilters);
    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  const goToExpDetail = (id: number) => {
    navigate(`${siteRoutes.experienceDetail}?id=${id}`);
  };

  const handlePageChange = (pageInfo: { selected: number; limit: number }) => {
    filters.page = pageInfo.selected;
    filters.size = pageInfo.limit;
    getAllEvents(filters);
  };

  // Check if a tab is currently active
  const isTabActive = (tab: any) => {
    return selectedTab === tab.id;
  };

  return (
    <div className="container min-h-[70vh] mb-4">
      <AttendeeBreadcrumb links={breadcrumbLinks} />
      <h2 className="text-2xl text-blackie font-bold my-4">Experiences</h2>
      <div className="flex justify-between  flex-wrap items-center gap-5">
        <div className="flex flex-wrap gap-5 items-center">
          <div className="flex space-x-4 md:space-x-10 pb-2">
            {allFilterTabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => handleTabChange(tab.type, tab.value, tab.id)}
                className={`text-base font-semibold pb-1 flex items-center ${
                  isTabActive(tab)
                    ? "text-lightBlueColor border-b-2 border-lightBlueColor"
                    : "text-grayText hover:text-lightBlueColor"
                }`}
              >
                {tab.title}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="search"
              name="searchTitle"
              placeholder="Find Experience.."
              value={filters.searchTitle}
              onChange={handleFilterChange}
              className="w-full pl-10 pr-4 py-2 h-[51px] rounded-lg border border-inputsBorderColor outline-none"
            />
          </div>
          <button
            onClick={toggleDrawer}
            className="text-labelTextColor flex gap-1 font-medium"
          >
            Filter by <FilterBy />
          </button>

          {/* Applied Filters Display */}
          {Object.keys(appliedFilters).length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              {/* <span className="text-sm text-grayText">Applied:</span> */}
              {Object.entries(appliedFilters).map(([key, value]) => {
                const getDisplayLabel = (key: string, value: any) => {
                  if (key === "isSession") {
                    return value === "true" ? "Sessions" : "Workshops";
                  }
                  if (key === "ageGroups") {
                    return `${value}`.replace(/_/g, " ");
                  }
                  return `${key}: ${value}`;
                };

                return (
                  <div
                    key={key}
                    className="flex items-center gap-1 bg-yellowText/10 text-yellowText px-3 py-1 rounded-full text-sm"
                  >
                    <span className="capitalize">
                      {getDisplayLabel(key, value)}
                    </span>
                    <button
                      onClick={() => removeAppliedFilter(key)}
                      className="text-red-500 hover:text-red-700 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 my-6">
        {data.map((event) => (
          <EventCard event={event} key={event.id} />
        ))}
      </div>

      <div className="flex items-center justify-center ">
        <CustomMessageDisplay
          show={!isLoading && !data.length && !isInitialLoad}
          title={
            selectedTab === "upcoming"
              ? "No Upcoming Experiences"
              : selectedTab === "past"
              ? "No Past Experiences"
              : selectedTab === "wishlist"
              ? "No Wishlist Experiences"
              : selectedTab === "waitlisted"
              ? "No Waitlisted Experiences"
              : selectedTab === "completed"
              ? "No Completed Experiences"
              : "No Experiences Found"
          }
          slogan={
            selectedTab === "upcoming"
              ? "You have no upcoming experiences scheduled. Browse and join new experiences!"
              : selectedTab === "past"
              ? "Your past experiences will appear here once they're completed"
              : selectedTab === "wishlist"
              ? "You haven't added any experiences to your wishlist yet. Start exploring!"
              : selectedTab === "waitlisted"
              ? "You're not on any waitlists. Check out popular experiences to join!"
              : selectedTab === "completed"
              ? "You haven't completed any experiences yet. Join one to get started!"
              : "No experiences available at the moment"
          }
          image={eventNotFoundGif}
          imageAlt="No experiences found"
          className="py-12"
          imageClassName="w-[150px] h-[120px]"
          titleClassName="text-2xl font-bold text-blackie"
          sloganClassName="text-gray-500 text-base"
        />
      </div>

      {data?.length > 0 && (
        <Pagination
          count={filters.totalElements}
          page={filters.page}
          limit={filters.size}
          onPageChange={handlePageChange}
          activeBgColor={"#DE9C27"}
          activeTextColor={"#ffffff"}
        />
      )}
      {drawerOpen && (
        <FilterDrawer
          isOpen={drawerOpen}
          onClose={toggleDrawer}
          onApply={onApplyDrawerFilters}
          currentFilters={appliedFilters}
        />
      )}
    </div>
  );
};

export default AttendeeExperiences;
